import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";

import { SiteNav, SiteFooter } from "../../components/fragments/ProductChrome";
import { Container } from "../../components/styles/Layout";

import useSeo from "../../hooks/useSeo";
import useJsonLd from "../../hooks/useJsonLd";
import { housePricesIndexSeo } from "../../data/seo";
import { brandForHost, brandForDistrict } from "../../data/brand";
import { housePricesIndexSchema } from "../../data/structuredData";

/*
 * The index of district pages.
 *
 * It exists because an orphan page is a page nobody finds. Eighty-one district
 * pages with no route to them from anywhere on the site would be crawled
 * slowly if at all, however good each one is - so this page, linked from the
 * footer, is what gives them all a path from the home page.
 */

const Header = styled.div`
    background: ${(props) => props.theme.darkgradient};
    color: white;
    padding: 3rem 0;
    margin-bottom: 2.5rem;
`;
const Title = styled.h1`
    margin: 0 0 0.75rem 0;
    color: white;
    font-size: 2rem;
    font-weight: 400;
    letter-spacing: 1px;
`;
const Lede = styled.p`
    margin: 0;
    max-width: 46rem;
    color: white;
    font-size: 1rem;
    line-height: 1.6;
`;

const AreaName = styled.h2`
    font-size: 1.05rem;
    font-weight: 600;
    color: ${(props) => props.theme.darkblue};
    margin: 1.75rem 0 0.6rem;
`;

/* Codes are short, so they tile rather than stack - the whole list stays on
   one screen on a desktop and two on a phone. */
const Grid = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(5.5rem, 100%), 1fr));
    gap: 0.5rem;
`;
const Cell = styled.li`
    a {
        display: block;
        text-align: center;
        padding: 0.55rem 0.4rem;
        border: 1px solid #e6e9ee;
        border-radius: 6px;
        background: #fff;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
        text-decoration: none;
        &:hover {
            background: #eef2f7;
        }
    }
`;

const Note = styled.p`
    margin: 2rem 0 3rem;
    font-size: 0.82rem;
    line-height: 1.55;
    color: ${(props) => props.theme.grey};
    max-width: 46rem;
`;

export default function HousePricesIndex() {
    const [districts, setDistricts] = useState([]);

    useEffect(() => {
        let live = true;
        axios
            .get(process.env.REACT_APP_API_URL + "property-data/house-prices")
            .then((res) => {
                if (live) setDistricts(res.data.districts || []);
            })
            .catch(() => {
                if (live) setDistricts([]);
            });
        return () => {
            live = false;
        };
    }, []);

    /*
     * ONLY THIS BRAND'S DISTRICTS. The API returns all 1,934 because both
     * brands read the same collection, and the build bakes one API hostname
     * into both bundles - so the server cannot tell which brand is asking and
     * the filtering has to happen here.
     *
     * Listing the other brand's districts would publish links that each
     * canonicalise to the other domain, which is the cross-brand leak the
     * split exists to remove.
     */
    const brand = brandForHost(typeof window === "undefined" ? "" : window.location.hostname);
    const mine = districts.filter((d) => brandForDistrict(d).key === brand.key);

    /*
     * Was STATIC_SEO plus ORIGIN, and ORIGIN is the agency's unconditionally -
     * so on doorstepdata.co.uk this overwrote the renderer's correct canonical
     * with property-people.co.uk the moment React booted. Invisible to curl,
     * visible to anything that runs JavaScript, which includes Google.
     */
    useSeo(housePricesIndexSeo(brand, mine.length));
    useJsonLd(mine.length ? housePricesIndexSchema(mine, brand.origin) : null);

    const byArea = {};
    mine.forEach((d) => {
        const area = (d.match(/^[A-Z]+/) || ["?"])[0];
        (byArea[area] = byArea[area] || []).push(d);
    });

    return (
        <>
            <SiteNav />
            <Header>
                <Container>
                    <Title>House prices by postcode district</Title>
                    {/* The visible copy has to agree with the title tag and the
                        canonical above it. A national page promising "south
                        west London and Surrey" reads as the wrong site. */}
                    <Lede>
                        Median sold prices, ten-year trends and the most recent sales for{" "}
                        {mine.length ? mine.length.toLocaleString("en-GB") + " postcode districts" : "every postcode district we cover"}{" "}
                        across{" "}
                        {brand.key === "product" ? "England and Wales" : "south west London and Surrey"},
                        computed from HM Land Registry Price Paid data.
                    </Lede>
                </Container>
            </Header>
            <Container>
                {Object.keys(byArea)
                    .sort()
                    .map((area) => (
                        <div key={area}>
                            <AreaName>{area}</AreaName>
                            <Grid>
                                {byArea[area].map((d) => (
                                    <Cell key={d}>
                                        <Link to={"/house-prices/" + d.toLowerCase()}>{d}</Link>
                                    </Cell>
                                ))}
                            </Grid>
                        </div>
                    ))}
                <Note>
                    Contains HM Land Registry data &copy; Crown copyright and database right{" "}
                    {new Date().getFullYear()}, licensed under the Open Government Licence v3.0. For one
                    address rather than a district, use the{" "}
                    <Link to="/property-report">free property report</Link>.
                </Note>
            </Container>
            <SiteFooter />
        </>
    );
}
